import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useImperativeHandle, type Ref } from 'react';
import { Controller, useForm, type Path } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';

import { ApiError } from '@/api/envelope';
import { applyServerErrors } from '@/api/serverErrors';
import { ROUTES } from '@/app/routes';
import { ContactFields } from '@/components/form/ContactFields';
import { GeographicScopeFields } from '@/components/form/GeographicScopeFields';
import { SocialLinksEditor } from '@/components/form/SocialLinksEditor';
import { UnsavedChangesDialog } from '@/components/form/UnsavedChangesDialog';
import { useUnsavedChanges } from '@/components/form/useUnsavedChanges';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { ORGANIZATION_TYPES } from '@/lib/organization';

import {
  EMPTY_ORGANIZATION,
  STARTING_STATUSES,
  isHandleConflict,
  organizationEditFormSchema,
  organizationFormSchema,
  type OrganizationDirtyFields,
  type OrganizationFormOutput,
  type OrganizationFormValues,
} from './organizationSchema';

/**
 * The server's field names to the form's, in page order.
 *
 * The server names each refused field by the last part of its path alone, so
 * the contact arrives as "email", "phone" and "title", and its "name" cannot
 * be told from the organization's - the organization's wins, as the far more
 * likely refusal.
 */
const SERVER_FIELDS: Record<string, Path<OrganizationFormValues>> = {
  name: 'name',
  slug: 'slug',
  organization_type: 'organization_type',
  description: 'description',
  website: 'website',
  geographic_scope: 'geographic_scope',
  geographic_states: 'geographic_states',
  scope_description: 'scope_description',
  email: 'contact_email',
  phone: 'contact_phone',
  title: 'contact_title',
  social_links: 'social_links',
  status: 'status',
};

/** What the caller can do to the form from outside it. */
export interface OrganizationFormHandle {
  /** Replaces every value and marks the form clean, so Save turns off again. */
  reset: (values: OrganizationFormValues) => void;
  /**
   * Puts a refusal's field messages on their fields and focuses the first;
   * returns whatever concerned no field, for the page's form-level alert.
   */
  applyServerErrors: (error: unknown) => { remainder: string | null };
}

interface OrganizationFormProps {
  /**
   * Creating asks for a starting status and can build a handle from the name.
   * Editing does neither: the status has its own dialog, and an account that
   * already exists has a handle that links depend on.
   */
  mode?: 'create' | 'edit';
  defaultValues?: OrganizationFormValues;
  /**
   * Handed the validated values and which fields were touched.
   *
   * Editing sends only what changed, and the validated values cannot show that
   * on their own: a field that was cleared and a field that never held anything
   * both arrive as undefined.
   */
  onSubmit: (
    values: OrganizationFormOutput,
    dirtyFields: OrganizationDirtyFields,
  ) => void;
  pending: boolean;
  submitLabel: string;
  /**
   * True when the form sits inside a CollapsibleCard, which is already a card.
   * Its sections then become headed blocks rather than cards of their own.
   */
  embedded?: boolean;
  /** Lets the caller reset the form to a record the server has confirmed. */
  ref?: Ref<OrganizationFormHandle>;
}

function Section({
  title,
  embedded,
  testId,
  children,
}: {
  title: string;
  testId: string;
  /**
   * Inside a CollapsibleCard the whole form is already one card, so a card
   * per section would be a card inside a card. The heading and a rule above
   * it separate them instead.
   */
  embedded?: boolean;
  children: React.ReactNode;
}) {
  // One level below whatever heads the form: the page title when it stands
  // alone, the card's own heading when it sits inside one. A fixed level
  // skipped one on the create page, which has no card heading between.
  const heading = (
    <Typography
      variant="subtitle2"
      component={embedded ? 'h3' : 'h2'}
      sx={{ fontWeight: 600, mb: 2, color: embedded ? 'text.secondary' : undefined }}
    >
      {title}
    </Typography>
  );

  if (embedded) {
    return (
      <Box
        data-testid={testId}
        sx={{
          '&:not(:first-of-type)': { borderTop: 1, borderColor: 'divider', pt: 3 },
        }}
      >
        {heading}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>{children}</Box>
      </Box>
    );
  }

  return (
    <Paper variant="outlined" data-testid={testId} sx={{ p: 3 }}>
      {heading}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>{children}</Box>
    </Paper>
  );
}

/**
 * The organization form, used for creating an account and later for editing one.
 *
 * It takes its values and its submit handler from the caller so the Edit page
 * can reuse it with an account's current details filled in; building it any
 * other way would mean writing the same form twice.
 */
export function OrganizationForm({
  mode = 'create',
  defaultValues = EMPTY_ORGANIZATION,
  onSubmit,
  pending,
  submitLabel,
  embedded,
  ref,
}: OrganizationFormProps) {
  const editing = mode === 'edit';

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setError,
    setFocus,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<OrganizationFormValues, unknown, OrganizationFormOutput>({
    resolver: zodResolver(editing ? organizationEditFormSchema : organizationFormSchema),
    defaultValues,
  });

  // Resetting and error placement are driven by the caller: only the page
  // knows a save came back or was refused, but only this form knows its
  // fields. A 409 is a clash, which lands whole on the field it is about: the
  // web handle when the backend's words name the slug, else the name.
  useImperativeHandle(
    ref,
    () => ({
      reset: (values) => reset(values),
      applyServerErrors: (error) =>
        applyServerErrors(error, { setError, setFocus }, SERVER_FIELDS, {
          conflictField:
            error instanceof ApiError && isHandleConflict(error.serverMessage ?? '')
              ? 'slug'
              : 'name',
        }),
    }),
    [reset, setError, setFocus],
  );

  // The same flag the Save button reads, so the prompt and the button cannot
  // disagree about whether anything is unsaved. Create mode counts too: typing
  // into a new account's form is as easy to lose as an edit.
  const unsaved = useUnsavedChanges(isDirty);

  return (
    <Box
      component="form"
      onSubmit={(event) =>
        void handleSubmit((values) =>
          // React Hook Form types an array field's flag as an array of flags,
          // which is more than any caller here needs to know.
          onSubmit(values, dirtyFields as OrganizationDirtyFields),
        )(event)
      }
      sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 760 }}
      noValidate
      data-testid="org-form"
    >
      <Section
        title="The organization"
        embedded={embedded}
        testId="org-form-organization-section"
      >
        {/* A server refusal sits here through setError until the name is
            edited, when re-validation replaces it - so it never lingers under
            a brand new name claiming that one is taken too. */}
        <Field label="Name" error={errors.name?.message} testId="org-form-name">
          <TextField {...register('name')} />
        </Field>

        <Field
          label="Web handle"
          hint={
            editing
              ? 'Changing this breaks any link that uses the old handle.'
              : 'Leave blank to build one from the name. It appears in links, so it is awkward to change later.'
          }
          error={errors.slug?.message}
          testId="org-form-slug"
        >
          <TextField placeholder="sierra-club" {...register('slug')} />
        </Field>

        {/* Controlled rather than registered. A select given a defaultValue reads
            it once, when the form first appears, so a reset after a save would
            leave it showing the choice it started with. */}
        <Controller
          name="organization_type"
          control={control}
          render={({ field }) => (
            <Field
              label="Type"
              error={errors.organization_type?.message}
              testId="org-form-type"
            >
              <TextField select {...field} value={field.value ?? ''}>
                <MenuItem value="">Not set</MenuItem>
                {ORGANIZATION_TYPES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Field>
          )}
        />

        <Field
          label="Description"
          error={errors.description?.message}
          testId="org-form-description"
        >
          <TextField multiline minRows={3} {...register('description')} />
        </Field>

        <Field label="Website" error={errors.website?.message} testId="org-form-website">
          <TextField placeholder="https://example.org" {...register('website')} />
        </Field>
      </Section>

      <Section
        title="Where they work"
        embedded={embedded}
        testId="org-form-where-section"
      >
        <GeographicScopeFields
          testId="org-form-geo"
          control={control}
          names={{
            scope: 'geographic_scope',
            states: 'geographic_states',
            note: 'scope_description',
          }}
          register={register}
          watch={watch}
          errors={errors}
        />
      </Section>

      <Section
        title="Primary contact"
        embedded={embedded}
        testId="org-form-contact-section"
      >
        <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
          Who Activist Technologies deals with. Not shown in the app.
        </Typography>

        {/* No prefix, so these stay "Contact name" and the rest, exactly as
            they read before the billing form needed the same four boxes. */}
        <ContactFields
          testId="org-form-contact"
          names={{
            name: 'contact_name',
            title: 'contact_title',
            email: 'contact_email',
            phone: 'contact_phone',
          }}
          register={register}
          errors={errors}
        />
      </Section>

      <Section title="Social links" embedded={embedded} testId="org-form-social-section">
        <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
          Shown on the organization's page in the app.
        </Typography>

        <SocialLinksEditor
          control={control}
          name="social_links"
          errors={errors}
          testId="org-form-social-links"
        />
      </Section>

      <Section
        title="Account status"
        embedded={embedded}
        testId="org-form-status-section"
      >
        {editing ? (
          /* Not a field. Changing a live account's status has consequences and
             has to be given a reason, so it is done from the account card and
             not by quietly re-saving this form. */
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="org-form-status-message"
          >
            The status is changed from the account card, where a reason is recorded with
            it.
          </Typography>
        ) : (
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Field
                label="Start as"
                hint="Billing, contract and plan details are set later, on the account itself."
                error={errors.status?.message}
                testId="org-form-status"
              >
                <TextField select {...field} value={field.value ?? 'PENDING'}>
                  {STARTING_STATUSES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Field>
            )}
          />
        )}
      </Section>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          type="submit"
          loading={pending}
          disabled={editing && !isDirty}
          data-testid="org-form-submit-button"
        >
          {submitLabel}
        </Button>
        <Button
          component={RouterLink}
          to={ROUTES.staff.organizations}
          variant="secondary"
          data-testid="org-form-cancel-link"
        >
          Cancel
        </Button>
      </Box>

      <UnsavedChangesDialog {...unsaved} />
    </Box>
  );
}
